/* eslint-disable modernloop/restrict-imports.cjs */
import React from 'react';

import { logError } from '@modernloop/shared/utils';

import SmartRecruitersCreateCandidatePermissionDialog from 'src/views-new/JobDetailsPage/SourcingLinks/AtsCreateCandidatePermissionDialogs/SmartRecruitersCreateCandidatePermissionDialog';
import SmartRecruitersCard, {
  Props as SmartRecruitersProps,
} from 'src/views-new/Settings/Integrations/Cards/SmartRecruitersCard';

import { ListIntegrationResponse } from '../api/integration/useIntegrations';

import AtsSevice, { ExtensionSidebarParsedUrlType, ParsedAtsUrlResult, ScorecardGroupByStrategy } from './AtsService';

/**
 * @description
 * Extract candidate id from the url
 * @param url :  https://www.smartrecruiters.com/app/people/candidates/88c23b0a-92f0-4960-a89e-ab7e46859c11/overview
 * @returns candidate id : 88c23b0a-92f0-4960-a89e-ab7e46859c11
 */
const getCandidateIdFromUrl = (url: URL): string | null => {
  const folders = url.pathname.split('/') || [];

  const candidatesIndex = folders.indexOf('candidates');

  if (candidatesIndex > 0) {
    return folders[candidatesIndex + 1];
  }

  return null;
};

/**
 * @description
 * Extract application id from the url
 * @param url :  https://www.smartrecruiters.com/app/people/applications/88c23b0a-92f0-4960-a89e-ab7e46859c11/overview
 * @returns application id : 88c23b0a-92f0-4960-a89e-ab7e46859c11
 */
const getApplicationIdFromUrl = (url: URL): string | null => {
  const folders = url.pathname.split('/') || [];
  const applicationIndex = folders.indexOf('applications');

  if (applicationIndex > 0) {
    return folders[applicationIndex + 1];
  }

  return null;
};
/* *
 * SmartRecruiters ATS service
 * Imp: Never have atsType set in ATS service
 * */
class SmartRecruitersAtsService implements AtsSevice {
  readonly featureFlag = 'user_smart_recruiters_integration';

  readonly name = 'SmartRecruiters';

  readonly hostname = 'smartrecruiters'; // ex. https://www.smartrecruiters.com/app/home/

  readonly logoUrl = `/static/images/integrations/smartrecruiters.png`;

  readonly helpCenterUrl = '';

  readonly scorecardLabelPluralTranslationKey = 'hiring-dashboard-common:common.ats.scorecardsFallback';

  readonly noScorecardLabelTranslationKey = 'hiring-dashboard-common:common.ats.noScorecardFallback';

  readonly scorecardSelectTitleTranslationKey = 'hiring-dashboard-nav:nav.scorecards.selectTitle';

  readonly scorecardSearchPlaceholderTranslationKey = 'hiring-dashboard-nav:nav.scorecards.searchPlaceholder';

  readonly scorecardSearchPlaceholderEllipsisTranslationKey =
    'hiring-dashboard-nav:nav.scorecards.searchPlaceholderEllipsis';

  readonly isSourcingLinkEnabled = true;

  readonly isAtsUserSelectEnabled = false;

  readonly isAtsImportAvailabilityEnabled = false;

  readonly skipInterviewKitLinkTokenValidation = false;

  readonly scorecardGroupByKey = null;

  readonly scorecardGroupByStrategy = ScorecardGroupByStrategy.AtsJobStage;

  readonly scorecardSortKey = null;

  readonly showEstimatedMinutes = false;

  readonly createCandidatePermissionDialog = SmartRecruitersCreateCandidatePermissionDialog;

  readonly showRequisitionId = false;

  readonly canSupportBrightHire = true;

  readonly hasScorecardSync = false;

  readonly scorecardSyncSettingLabelTranslationKey =
    'hiring-dashboard-common:common.ats.keepInterviewsInSyncWithAtsInterviewPlan';

  readonly hasHiringProcess = true;

  readonly hasScorecards = false;

  readonly hasEnforcedJobPermissions = true;

  readonly resetInterviewPlanWarningMessage =
    'The interview plan for this stage will be reset to its current settings in SmartRecruiters.';

  // eslint-disable-next-line class-methods-use-this
  getIntegrationCard = (props: SmartRecruitersProps, integrations: ListIntegrationResponse): JSX.Element => {
    return <SmartRecruitersCard {...props} smartRecruiters={integrations?.smart_recruiters} />;
  };

  parseExtensionSidebarUrl = (externalUrl: string): ParsedAtsUrlResult | null => {
    try {
      const url: URL | undefined = new URL(externalUrl);

      // check if url is smart recruiters url
      if (!url.hostname.includes(this.hostname)) {
        return null; // exit early
      }

      const atsCandidateId = getCandidateIdFromUrl(url);
      const atsApplicationId = getApplicationIdFromUrl(url);

      if (atsCandidateId || atsApplicationId) {
        return {
          type: ExtensionSidebarParsedUrlType.Candidate,
          atsCandidateId,
          atsApplicationId,
        };
      }
    } catch (error) {
      logError('Error parsing Smart Recruiters ATS url', error);
    }

    return null;
  };
}

export default new SmartRecruitersAtsService();
